import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { soccerPath } from '../utils/paths';
import PhotoGallery from './PhotoGallery';

const TrainingGroupPanel = ({ userId, activePlan, onPlanChanged }) => {
  const [memberships, setMemberships] = useState([]);
  const [selectedClubId, setSelectedClubId] = useState('');
  const [challenge, setChallenge] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createName, setCreateName] = useState('Club Stamina Challenge');
  const [createTemplate, setCreateTemplate] = useState('STAMINA_4WEEK');

  const loadClubData = useCallback(async () => {
    if (!userId || !selectedClubId) return;
    try {
      setLoading(true);
      setError('');
      const [active, board, clubFeed] = await Promise.all([
        api.getActiveTrainingChallenge(selectedClubId, userId).catch(() => null),
        api.getActiveTrainingChallenge(selectedClubId, userId)
          .then((c) => (c ? api.getTrainingLeaderboard(selectedClubId, c.id, userId) : []))
          .catch(() => []),
        api.getTrainingClubFeed(selectedClubId, userId).catch(() => []),
      ]);
      setChallenge(active);
      setLeaderboard(board || []);
      setFeed(clubFeed || []);
    } catch (err) {
      setError(err.message || 'Failed to load club training');
    } finally {
      setLoading(false);
    }
  }, [userId, selectedClubId]);

  useEffect(() => {
    if (!userId) return;
    api.getMyClubMemberships(userId).then((data) => {
      const approved = (data || []).filter((m) => m.status === 'APPROVED');
      setMemberships(approved);
      if (approved.length && !selectedClubId) {
        setSelectedClubId(approved[0].clubId);
      }
    });
  }, [userId, selectedClubId]);

  useEffect(() => {
    loadClubData();
  }, [loadClubData]);

  const handleJoin = async () => {
    if (!challenge?.id) return;
    try {
      setSaving(true);
      setError('');
      await api.joinTrainingChallenge(challenge.id, userId, { showOnLeaderboard: true });
      setSuccess('Joined the club challenge! Your plan is synced.');
      onPlanChanged?.();
      await loadClubData();
    } catch (err) {
      setError(err.message || 'Failed to join challenge');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!selectedClubId) return;
    try {
      setSaving(true);
      setError('');
      await api.createTrainingChallenge(selectedClubId, userId, {
        name: createName,
        templateId: createTemplate,
        startDate: new Date().toISOString().slice(0, 10),
      });
      setSuccess('Club challenge created.');
      await loadClubData();
    } catch (err) {
      setError(err.message || 'Failed to create challenge');
    } finally {
      setSaving(false);
    }
  };

  const handleOptInToggle = async () => {
    try {
      setSaving(true);
      await api.updateTrainingLeaderboardOptIn(userId, !activePlan?.showOnLeaderboard);
      setSuccess(activePlan?.showOnLeaderboard ? 'Removed from leaderboard.' : 'Added to leaderboard.');
    } catch (err) {
      setError(err.message || 'Failed to update leaderboard setting');
    } finally {
      setSaving(false);
    }
  };

  const handleShareChallenge = () => {
    if (!challenge?.shareUrl) return;
    navigator.clipboard?.writeText(challenge.shareUrl);
    setSuccess('Challenge link copied.');
  };

  if (!memberships.length) {
    return (
      <section className="training-panel">
        <h2>Group training</h2>
        <p className="training-muted">
          Join a club to participate in group stamina challenges and share progress with teammates.
        </p>
        <a href={soccerPath('/clubs/list')} className="training-inline-link">
          Browse clubs →
        </a>
      </section>
    );
  }

  return (
    <section className="training-panel" aria-label="Group training">
      <div className="training-panel__header">
        <div>
          <h2>Group training</h2>
          <p>Club challenges, leaderboard, and shared training feed.</p>
        </div>
        <label>
          Club
          <select value={selectedClubId} onChange={(e) => setSelectedClubId(e.target.value)}>
            {memberships.map((m) => (
              <option key={m.clubId} value={m.clubId}>
                {m.clubName}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}
      {loading && <p className="training-muted">Loading club challenge…</p>}

      {!loading && !challenge && (
        <div className="training-group-empty">
          <p>No active challenge for this club.</p>
          <div className="training-create-challenge">
            <label>
              Challenge name
              <input value={createName} onChange={(e) => setCreateName(e.target.value)} />
            </label>
            <label>
              Program
              <select value={createTemplate} onChange={(e) => setCreateTemplate(e.target.value)}>
                <option value="STAMINA_4WEEK">4-Week Stamina Builder</option>
                <option value="INTERMEDIATE">Intermediate (1 week)</option>
                <option value="BEGINNER">Beginner (1 week)</option>
              </select>
            </label>
            <button type="button" className="btn btn-primary" onClick={handleCreate} disabled={saving}>
              Create club challenge
            </button>
            <p className="training-muted">Club admins can start a new group challenge.</p>
          </div>
        </div>
      )}

      {!loading && challenge && (
        <>
          <div className="training-challenge-card">
            <h3>{challenge.name}</h3>
            <p>{challenge.description || `${challenge.templateId} · ${challenge.participantCount} participants`}</p>
            <p className="training-muted">
              {challenge.startDate} – {challenge.endDate}
            </p>
            <div className="training-actions">
              {activePlan?.challengeId !== challenge.id && (
                <button type="button" className="btn btn-primary" onClick={handleJoin} disabled={saving}>
                  Join challenge
                </button>
              )}
              {activePlan?.challengeId === challenge.id && (
                <button type="button" className="btn btn-secondary" onClick={handleOptInToggle} disabled={saving}>
                  {activePlan.showOnLeaderboard ? 'Hide from leaderboard' : 'Show on leaderboard'}
                </button>
              )}
              {challenge.shareUrl && (
                <button type="button" className="btn btn-secondary" onClick={handleShareChallenge}>
                  Copy public link
                </button>
              )}
            </div>
          </div>

          <div className="training-leaderboard">
            <h3>Leaderboard</h3>
            {!leaderboard.length && <p className="training-muted">No leaderboard entries yet.</p>}
            <ol>
              {leaderboard.map((entry) => (
                <li key={entry.userId}>
                  <span className="training-leaderboard__rank">#{entry.rank}</span>
                  <div>
                    <strong>{entry.displayName}</strong>
                    <span>
                      {entry.completedSessions} sessions · streak {entry.currentStreak} · {entry.totalMinutes} min
                    </span>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="training-club-feed">
            <h3>Club feed</h3>
            <p className="training-muted">Sessions shared with the club (not public).</p>
            {!feed.length && <p className="training-muted">No club-shared sessions yet.</p>}
            <ul>
              {feed.map((item) => (
                <li key={item.logId} className="training-feed-item">
                  <div>
                    <strong>{item.displayName}</strong>
                    <span>
                      {item.date} · {item.completedCount}/{item.totalCount} activities
                    </span>
                    {item.notes && <p>{item.notes}</p>}
                  </div>
                  {item.photoUrls?.length > 0 && <PhotoGallery photos={item.photoUrls} />}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </section>
  );
};

export default TrainingGroupPanel;
